import React from 'react';
import { toast } from 'react-toastify';
import { buildGlobalSettingsPatch, IGlobalStorefrontSettings, IStorefrontSettings } from '../../../../models/Storefront';
import StorefrontsTable from '../../../../components/Storefront/StorefrontsTable';
import { useGetGlobalStorefrontSettingsQuery, usePatchGlobalStorefrontSettingsMutation, useSoftDeleteStorefrontMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import StorefrontDropdown from '../../../../components/Storefront/StorefrontDropdown';
import StorefrontDomainDropdown from '../../../../components/Storefront/StorefrontDomainDropdown';
import { DefaultButton, Dialog, DialogFooter, Separator, Stack, StackItem } from '@fluentui/react';
import ToggleField from '../../../../components/Common/ToggleField';
import { useHistory, useLocation } from 'react-router-dom';
import NewStorefrontDialog, { CopyStorefrontDialog } from '../../../../components/Storefront/NewStorefrontPanel';
import StorefrontTemplatePickerModal, { IStorefrontTemplatePresets } from '../../../../components/Storefront/Templates/StorefrontTemplatePickerModal';
import SaveStorefrontTemplateModal from '../../../../components/Storefront/Templates/SaveStorefrontTemplateModal';
import EditStorefrontTemplateModal from '../../../../components/Storefront/Templates/EditStorefrontTemplateModal';
import { IStorefrontTemplate } from '../../../../models/Storefront';
import useCan, { STOREFRONT_TEMPLATES } from '../../../../permissions/ability';
import AiStorefrontCreateFlow from '../../../../components/Storefront/AdaptiveIntelligenceCreate/AiStorefrontCreateFlow';

/** The Getting Started checklist deep-links back into a running generation with this query param. */
const GENERATION_SESSION_PARAM = 'generationSession';

const StorefrontListPage: React.FC = () => {
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    const [patchGlobalSettings] = usePatchGlobalStorefrontSettingsMutation();
    const [softDeleteStorefront] = useSoftDeleteStorefrontMutation();
    const history = useHistory();
    const location = useLocation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IGlobalStorefrontSettings>();
    const localSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const originalSettingsRef = React.useRef<IGlobalStorefrontSettings>();

    const [templatePicker, setTemplatePicker] = React.useState(false);
    const [newStorefront, setNewStorefront] = React.useState(false);
    const [selectedTemplate, setSelectedTemplate] = React.useState<IStorefrontTemplate | undefined>();
    // Starting palette / font pack chosen on the template details view. Undefined = keep the
    // template's captured defaults.
    const [templatePresets, setTemplatePresets] = React.useState<IStorefrontTemplatePresets | undefined>();
    const [copyStorefront, setCopyStorefront] = React.useState(false);
    const [selectedCopyStorefront, setSelectedCopyStorefront] = React.useState<IStorefrontSettings | undefined>();
    const [confirmDelete, setConfirmDelete] = React.useState<IStorefrontSettings | undefined>();
    const [saveTemplateFor, setSaveTemplateFor] = React.useState<IStorefrontSettings | undefined>();
    const [editTemplateId, setEditTemplateId] = React.useState<string | undefined>();
    const canTemplates = useCan(STOREFRONT_TEMPLATES);
    const [aiCreateOpen, setAiCreateOpen] = React.useState(false);
    // Set only when resuming an existing generation, so a fresh run starts on the form.
    const [aiSessionId, setAiSessionId] = React.useState<string | undefined>();

    // ?generationSession={id} opens the flow straight on that session's progress view. The param is
    // stripped once it has been read: leaving it in the URL would reopen the dialog every time the
    // user closed it, and would resurrect it on a refresh long after the session was done.
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get(GENERATION_SESSION_PARAM);
        if (!sessionId) return;
        setAiSessionId(sessionId);
        setAiCreateOpen(true);
        params.delete(GENERATION_SESSION_PARAM);
        const remaining = params.toString();
        history.replace(`${location.pathname}${remaining ? `?${remaining}` : ''}`);
    }, [location.search, location.pathname, history]);

    // Reopening a running generation from the storefronts table. The same two pieces of state the
    // ?generationSession deep link sets, so a row and a link land on exactly the same progress view.
    const resumeGeneration = React.useCallback((sessionId: string) => {
        setAiSessionId(sessionId);
        setAiCreateOpen(true);
    }, []);

    const closeAiCreate = React.useCallback(() => {
        setAiCreateOpen(false);
        setAiSessionId(undefined);
    }, []);

    const setLocalSettings = React.useCallback((s: IGlobalStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (globalSettingsQuery.data && !originalSettingsRef.current) {
            setLocalSettings(globalSettingsQuery.data);
            originalSettingsRef.current = globalSettingsQuery.data;
        }
    }, [globalSettingsQuery.data, setLocalSettings]);

    const doSave = React.useCallback(async (current: IGlobalStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildGlobalSettingsPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchGlobalSettings(patch).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    }, [patchGlobalSettings]);

    const handleDetailsClick = (storefront: IStorefrontSettings) => {
        history.push(`/settings/storefront/storefronts/${storefront.id}/dashboard`);
    };

    if (globalSettingsQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <h1>Storefronts</h1>
            <Stack tokens={{ childrenGap: '1em' }}>
                <Stack horizontal tokens={{ childrenGap: '1em' }}>
                    <StackItem styles={{ root: { width: 300 } }}>
                        <StorefrontDropdown
                            label='Default Storefront'
                            selected={localSettings.defaultStorefrontSubUrl}
                            onChange={(v) => {
                                const updated = { ...(localSettingsRef.current ?? localSettings), defaultStorefrontSubUrl: v };
                                setLocalSettings(updated);
                                doSave(updated);
                            }}
                            defaultLabel={'NOT SET'}
                        />
                    </StackItem>
                    <StackItem styles={{ root: { width: 300 } }}>
                        <StorefrontDomainDropdown
                            label='Default Domain'
                            emptyLabel='NOT SET'
                            value={localSettings.defaultStorefrontDomain ?? ''}
                            onChange={(v) => {
                                const updated = { ...(localSettingsRef.current ?? localSettings), defaultStorefrontDomain: v };
                                setLocalSettings(updated);
                                doSave(updated);
                            }}
                        />
                    </StackItem>
                </Stack>
                <StackItem>
                    <ToggleField
                        settingId='storefront.list.usePreview'
                        inline
                        label='Enable Storefront Preview'
                        checked={localSettings.usePreview}
                        onChange={(e, v) => {
                            const updated = { ...(localSettingsRef.current ?? localSettings), usePreview: v ?? false };
                            setLocalSettings(updated);
                            doSave(updated);
                        }}
                        toolTip='When enabled, changes to Storefronts will be visible on a preview URL before being published to the main Storefront.'
                    />
                </StackItem>
            </Stack>
            <Separator />
            <StorefrontsTable
                onDetailsClick={handleDetailsClick}
                onNewClick={() => setTemplatePicker(true)}
                onCopyClick={(sf) => { setSelectedCopyStorefront(sf); setCopyStorefront(true); }}
                onDeleteClick={(sf) => setConfirmDelete(sf)}
                onSaveTemplateClick={canTemplates ? (sf) => setSaveTemplateFor(sf) : undefined}
                onResumeGeneration={resumeGeneration}
            />
            <StorefrontTemplatePickerModal
                isOpen={templatePicker}
                onDismiss={() => setTemplatePicker(false)}
                onSelectTemplate={(template, presets) => {
                    setSelectedTemplate(template);
                    setTemplatePresets(presets);
                    setTemplatePicker(false);
                    setNewStorefront(true);
                }}
                onEditTemplate={(template) => setEditTemplateId(template.id)}
                onSelectAdaptiveIntelligence={() => {
                    setTemplatePicker(false);
                    setAiSessionId(undefined);
                    setAiCreateOpen(true);
                }}
            />
            <AiStorefrontCreateFlow
                isOpen={aiCreateOpen}
                sessionId={aiSessionId}
                onDismiss={closeAiCreate}
                onStartOver={() => {
                    closeAiCreate();
                    // Straight back to the picker, so they can try again without hunting for the
                    // New Storefront command a second time.
                    setTemplatePicker(true);
                }}
                onCompleted={closeAiCreate}
            />
            {saveTemplateFor?.id && (
                <SaveStorefrontTemplateModal
                    isOpen={true}
                    storefront={{ id: saveTemplateFor.id, name: saveTemplateFor.name ?? '' }}
                    onDismiss={() => setSaveTemplateFor(undefined)}
                    onSaved={() => setSaveTemplateFor(undefined)}
                />
            )}
            {editTemplateId && (
                <EditStorefrontTemplateModal
                    isOpen={true}
                    templateId={editTemplateId}
                    onDismiss={() => setEditTemplateId(undefined)}
                />
            )}
            <NewStorefrontDialog
                isOpen={newStorefront}
                globalSettings={localSettings}
                template={selectedTemplate}
                templatePresets={templatePresets}
                onAddStorefront={() => { setNewStorefront(false); setSelectedTemplate(undefined); setTemplatePresets(undefined); }}
                onClose={() => { setNewStorefront(false); setSelectedTemplate(undefined); setTemplatePresets(undefined); }}
            />
            <CopyStorefrontDialog
                isOpen={copyStorefront}
                settings={selectedCopyStorefront}
                onAddStorefront={() => setCopyStorefront(false)}
                onClose={() => setCopyStorefront(false)}
            />
            <Dialog
                hidden={confirmDelete === undefined}
                modalProps={{
                    isBlocking: true,
                    onDismiss: () => setConfirmDelete(undefined),
                }}
                dialogContentProps={{
                    title: 'Confirm Delete',
                    subText: `Are you sure you want to delete ${confirmDelete?.name}? This action cannot be undone.`,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Confirm'
                        onClick={() => {
                            if (confirmDelete?.id)
                                softDeleteStorefront(confirmDelete.id).then(() => setConfirmDelete(undefined));
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setConfirmDelete(undefined)}
                    />
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default StorefrontListPage;